import { useEffect, useRef, useState } from "react";
import { Bell, X } from "lucide-react";
import api from "../../services/api";
import type { TenantNotification } from "../../types/notification";

import { Link } from "react-router-dom";

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

export function NotificationCenter() {
  const [notifications, setNotifications] = useState<TenantNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<TenantNotification | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response =
          await api.get<TenantNotification[]>("/notifications/me");
        setNotifications(Array.isArray(response.data) ? response.data : []);
      } catch {
        // A topbar não deve bloquear o restante do sistema em caso de falha.
      }
    };
    void load();
    const timer = window.setInterval(load, 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  async function handleNotification(notification: TenantNotification) {
    setNotifications((items) =>
      items.map((item) =>
        item.id === notification.id ? { ...item, read: true } : item,
      ),
    );
    if (notification.body) setSelected({ ...notification, read: true });
    try {
      await api.patch(`/notifications/${notification.id}/read`);
    } catch {
      setNotifications((items) =>
        items.map((item) =>
          item.id === notification.id ? notification : item,
        ),
      );
    }
  }

  const unreadCount = notifications.filter((item) => !item.read).length;

  return (
    <>
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="relative rounded-lg p-2 text-amber-400 transition hover:bg-blue-900 hover:text-amber-300"
          aria-label={`Notificações${unreadCount ? `, ${unreadCount} não lidas` : ""}`}
          aria-expanded={open}
        >
          <Bell size={21} fill="currentColor" />
          {unreadCount > 0 && (
            <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {open && (
          <div className="fixed left-3 right-3 top-[4.5rem] z-40 w-auto overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-[min(22rem,calc(100vw-2rem))]">
            <div className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-900">
              Notificações
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-500">
                  Nenhuma notificação.
                </p>
              ) : (
                notifications.map((notification) => (
                  <button
                    type="button"
                    key={notification.id}
                    onClick={() => void handleNotification(notification)}
                    className={`block w-full border-b border-slate-100 px-4 py-3 text-left transition hover:bg-slate-50 ${notification.read ? "bg-white" : "bg-amber-50"}`}
                  >
                    <span className="flex items-start gap-2">
                      {!notification.read && (
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                      )}
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-slate-900">
                          {notification.title}
                        </span>
                        <span className="mt-1 block text-xs text-slate-500">
                          {formatDateTime(notification.createdAt)} ·{" "}
                          {notification.read ? "Lida" : "Não lida"}
                        </span>
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notification-title"
        >
          <div className="modal-card-lg">
            <div className="flex items-start justify-between gap-4">
              <h2 id="notification-title" className="app-section-title">
                {selected.title}
              </h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {formatDateTime(selected.createdAt)}
            </p>
            <p className="mt-5 whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {selected.body}
            </p>
            {selected.protocolId && (
              <Link
                className="btn btn-primary mt-4"
                onClick={() => {
                  setSelected(null);
                  setOpen(false);
                }}
                to={`/painel/protocolos/${encodeURIComponent(selected.protocolId)}`}
              >
                Ver protocolo
              </Link>
            )}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="btn btn-secondary"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
